/**
 * Geographic resolutions a dataset can cover, stored on `embed_pages.geographic_resolution`
 * as a bitmask. Bit layout mirrors `hub_admin.admin_roles.crud_datasets` (first listed =
 * highest bit); a mask of 0 means the dataset has no geographic data.
 */
export const GeographicResolution = {
  SMALL_SCALE: 4,
  SUB_SOVEREIGN: 2,
  SOVEREIGN: 1,
} as const;

export type GeographicResolutionFlag = (typeof GeographicResolution)[keyof typeof GeographicResolution];

export const GEOGRAPHIC_RESOLUTION_LABELS: Record<GeographicResolutionFlag, string> = {
  [GeographicResolution.SMALL_SCALE]: "Small-scale",
  [GeographicResolution.SUB_SOVEREIGN]: "Sub-sovereign",
  [GeographicResolution.SOVEREIGN]: "Sovereign",
};

export const GEOGRAPHIC_RESOLUTION_OPTIONS: Array<{ label: string; value: GeographicResolutionFlag }> = (
  Object.values(GeographicResolution) as GeographicResolutionFlag[]
).map((value) => ({ label: GEOGRAPHIC_RESOLUTION_LABELS[value], value }));

export const hasGeographicResolution = (mask: number | null | undefined, flag: GeographicResolutionFlag): boolean =>
  ((mask ?? 0) & flag) === flag;

/** The individual flags set in a bitmask, for a checkbox group. */
export const toResolutionValues = (mask: number | null | undefined): GeographicResolutionFlag[] =>
  (Object.values(GeographicResolution) as GeographicResolutionFlag[]).filter((flag) =>
    hasGeographicResolution(mask, flag)
  );

/** The bitmask to store for a set of checkbox group values. */
export const fromResolutionValues = (values: readonly GeographicResolutionFlag[] | undefined): number =>
  (values ?? []).reduce((mask, flag) => mask | flag, 0);

/** Human-readable summary, e.g. "Sub-sovereign, Sovereign" or "No geographic data". */
export const formatGeographicResolution = (mask: number | null | undefined): string => {
  const labels = toResolutionValues(mask).map((flag) => GEOGRAPHIC_RESOLUTION_LABELS[flag]);
  return labels.length ? labels.join(", ") : "No geographic data";
};
